import { defineStore } from 'pinia'
import { ref } from 'vue'

import { useUrl } from '@/composables/url-helpers'
import { DEFAULT_CATEGORY_PAGE_SIZE, DEFAULT_MAIN_PAGE_SIZE } from '@/constants/general'
import { useI18nAddon } from '@/i18n/i18n'
import { MainCategoryListZipType } from '@/pages/Homepage/homepage-store'
import { MainCategoriesType } from '@/types'
import { shuffle, zipAPIGame } from '@/utils/helpers'
import { getGamesByCategories } from '@/utils/requests/games'

import { useAppStore } from '../app-store'
import { ShortMyGameZipType } from './utils'

const MY_GAMES_LIMIT = DEFAULT_CATEGORY_PAGE_SIZE * 3

export const useMyGamesStore = defineStore(
    'my-games',
    () => {
        const myGames = ref<ShortMyGameZipType[]>([])
        const i18nAddon = useI18nAddon()
        const appStore = useAppStore()
        const { getCategoryRoute } = useUrl()
        const trendingCategoriesWithGames = ref<Record<string, MainCategoryListZipType>>({})

        function update(inputGame: ShortMyGameZipType): void {
            const [hru] = inputGame
            const existingGame = myGames.value.find(([gameHru]) => gameHru === hru)
            /* put always input game for migration purposes */
            myGames.value = existingGame
                ? [inputGame, ...myGames.value.filter(([gameHru]) => gameHru !== hru)]
                : [inputGame, ...myGames.value].slice(0, MY_GAMES_LIMIT)
        }

        async function loadTrendingNowCategory(): Promise<void> {
            const locale = i18nAddon.currentLocale.value
            if (trendingCategoriesWithGames.value[locale]) {
                return
            }

            const data = await getGamesByCategories({
                categoriesIds: ['trending_now'],
                pagination: { page: 1, pageSize: DEFAULT_MAIN_PAGE_SIZE },
                platform: appStore.platform,
                withDebugInfo: appStore.isDebugMode,
                locale,
            })

            const [trending] = data.categories.map(({ id, hru, title, games }) => ({
                id: id as MainCategoriesType,
                title,
                location: getCategoryRoute({ hru, type: 'category' }),
                games: shuffle(games.map(zipAPIGame)),
            }))

            trendingCategoriesWithGames.value[locale] = trending
        }

        return {
            myGames,
            update,
            loadTrendingNowCategory,
            trendingCategoriesWithGames,
        }
    },
    {
        persist: {
            pick: ['myGames'],
            afterHydrate: (ctx) => {
                if (ctx.store._storageListenerAdded) {
                    return
                }
                ctx.store._storageListenerAdded = true
                window.addEventListener('storage', (e: StorageEvent) => {
                    if (e.key === ctx.store.$id) {
                        ctx.store.$hydrate()
                    }
                })
            },
        },
    },
)
